import { useEffect, useRef } from 'react';
import { Bold, Italic, Underline, Strikethrough, AlignLeft, AlignCenter, AlignRight } from 'lucide-react';

// Deliberately shorter than EditToolbar's list — this popover is sized for a
// phone. Every id here must be a real font id: an earlier version gave the
// "Arial" option id 'Helvetica', so both options carried the same <select>
// value and picking Arial snapped the control back to "Helvetica".
const FONTS = [
  { id: 'Arial', label: 'Arial' },
  { id: 'Helvetica', label: 'Helvetica' },
  { id: 'Times-Roman', label: 'Times' },
  { id: 'Courier', label: 'Courier' },
];

const FONT_SIZES = [8, 10, 12, 14, 16, 18, 20, 24, 28, 32, 36, 48];

const COLORS = [
  '#000000', '#F44336', '#2196F3', '#4CAF50', '#FF9800', '#9C27B0',
];

/**
 * TextFormatPopover — Floating popover for text formatting on mobile.
 * Anchored near the selected text box, not pinned to the toolbar.
 */
export default function TextFormatPopover({ anchorRect, editStyles, onUpdateStyle, onClose }) {
  const popRef = useRef(null);

  // Position: below the anchor, flip above if near bottom of viewport
  useEffect(() => {
    if (!popRef.current || !anchorRect) return;
    const pop = popRef.current;
    const popHeight = pop.offsetHeight;
    const viewH = window.innerHeight;
    const gap = 8;

    let top = anchorRect.bottom + gap;
    if (top + popHeight > viewH - 16) {
      top = anchorRect.top - popHeight - gap;
    }
    let left = Math.max(8, Math.min(anchorRect.left, window.innerWidth - pop.offsetWidth - 8));

    pop.style.top = `${top}px`;
    pop.style.left = `${left}px`;
  }, [anchorRect]);

  // Close on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (popRef.current && !popRef.current.contains(e.target)) onClose();
    };
    window.addEventListener('mousedown', handleClick);
    return () => window.removeEventListener('mousedown', handleClick);
  }, [onClose]);

  if (!anchorRect) return null;

  return (
    <div
      ref={popRef}
      className="fixed z-50 bg-white border border-gray-200 rounded-lg shadow-xl p-3"
      style={{ minWidth: '240px' }}
    >
      {/* Row 1: Font + Size */}
      <div className="flex gap-2 mb-2">
        <select
          value={editStyles.fontFamily}
          onChange={(e) => onUpdateStyle('fontFamily', e.target.value)}
          className="flex-1 h-8 px-2 text-xs border border-gray-300 rounded focus:outline-none focus:border-brand-blue"
        >
          {FONTS.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
        </select>
        <select
          value={editStyles.fontSize}
          onChange={(e) => onUpdateStyle('fontSize', parseInt(e.target.value, 10))}
          className="w-16 h-8 px-1 text-xs border border-gray-300 rounded focus:outline-none focus:border-brand-blue"
        >
          {FONT_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {/* Row 2: B, I, U, S, Alignment */}
      <div className="flex items-center gap-1 mb-2">
        <button
          onClick={() => onUpdateStyle('bold', !editStyles.bold)}
          className={`p-1.5 rounded ${editStyles.bold ? 'bg-brand-blue text-white' : 'text-gray-500 hover:bg-gray-100'}`}
          title="Bold"
        >
          <Bold size={16} />
        </button>
        <button
          onClick={() => onUpdateStyle('italic', !editStyles.italic)}
          className={`p-1.5 rounded ${editStyles.italic ? 'bg-brand-blue text-white' : 'text-gray-500 hover:bg-gray-100'}`}
          title="Italic"
        >
          <Italic size={16} />
        </button>
        <button
          onClick={() => onUpdateStyle('underline', !editStyles.underline)}
          className={`p-1.5 rounded ${editStyles.underline ? 'bg-brand-blue text-white' : 'text-gray-500 hover:bg-gray-100'}`}
          title="Underline"
        >
          <Underline size={16} />
        </button>
        <button
          onClick={() => onUpdateStyle('strikethrough', !editStyles.strikethrough)}
          className={`p-1.5 rounded ${editStyles.strikethrough ? 'bg-brand-blue text-white' : 'text-gray-500 hover:bg-gray-100'}`}
          title="Strikethrough"
        >
          <Strikethrough size={16} />
        </button>
        <div className="w-px h-5 bg-gray-200 mx-1" />
        {[
          { id: 'left', icon: AlignLeft, label: 'Align left' },
          { id: 'center', icon: AlignCenter, label: 'Align center' },
          { id: 'right', icon: AlignRight, label: 'Align right' },
        ].map((a) => (
          <button
            key={a.id}
            onClick={() => onUpdateStyle('alignment', a.id)}
            className={`p-1.5 rounded ${editStyles.alignment === a.id ? 'bg-brand-blue text-white' : 'text-gray-500 hover:bg-gray-100'}`}
            title={a.label}
          >
            <a.icon size={16} />
          </button>
        ))}
      </div>

      {/* Row 3: Colors */}
      <div className="flex gap-1.5 justify-center">
        {COLORS.map((c) => (
          <button
            key={c}
            onClick={() => onUpdateStyle('color', c)}
            className={`w-6 h-6 rounded-full border-2 transition-transform ${editStyles.color === c ? 'border-gray-800 scale-110' : 'border-transparent hover:scale-105'}`}
            style={{ backgroundColor: c }}
            title={c}
          />
        ))}
      </div>
    </div>
  );
}
